"use client";
import React from "react";
import { GameAlignmentType, LineType, OddVariant, PreGameOdds, Sport, buildOdd } from "common";
import Table, { ColumnDef } from "@/components/primitives/table";
import SportsbookOddsCell from "./sportsbook-cell";
import { formatDate, formatTime } from "@/utils";
import TeamDisplay from "@/components/team/display";
import Link from "next/link";
import { createGameDetailSlug } from "@/utils/url";

const SPORTSBOOK_URL_MAP: Record<string, string> = {
  DraftKings: "https://www.draftkings.com/",
  FanDuel: "https://www.fanduel.com/",
  SugarHousePA: "https://pa.playsugarhouse.com/",
  SugarHouseNJ: "https://nj.playsugarhouse.com/",
  Parx: "https://www.parxcasino.com/sportsbook/",
  RiversCasinoPA: "https://www.riverscasino.com/pittsburgh/sportsbook/",
  Caesars: "https://www.caesars.com/sports/",
  Consensus: "https://www.consensus.com/",
  BetMGM: "https://sports.betmgm.com/en/sports/",
  SportsIllustrated: "https://www.si.com/gambling/sportsbook/",
};

export default function OddsTable({
  sport,
  data,
  displayingOdds,
}: {
  sport: Sport;
  data?: PreGameOdds[];
  displayingOdds: OddVariant;
}) {
  const [processedData, setProcessedData] = React.useState<PreGameOdds[] | undefined>(data
    ? Array.isArray(data)
      ? data
      : [data]
    : undefined);
  // console.log("odds table:", data);
  // const normalizedData: PreGameOdds[] | undefined = data
  //   ? Array.isArray(data)
  //     ? data
  //     : [data]
  //   : undefined;
  const uniqueSportsbooks = React.useMemo(() => {
    // console.log("odds table normalized:", normalizedData);
    const allSportsbooks: string[] | undefined = processedData?.flatMap(
      (game) => game?.odds?.map((odd) => odd.sportsbook) ?? []
    );
    return Array.from(new Set(allSportsbooks));
  }, [data]);

  React.useEffect(() => {
    setProcessedData(data
      ? Array.isArray(data)
        ? data
        : [data]
      : undefined);
  }, []);
  const stickyCol1 = 80;
  const stickyCol2 = 96;
  console.log("ODDS TABLE DATA:", processedData);
  const columns: ColumnDef<PreGameOdds>[] = React.useMemo(
    () => [
      {
        header: "time",
        maxSize: 108,
        enableSorting: false,
        minWidth: "80px",
        width: "80px",
        maxWidth: "80px",
        meta: {
          classNames: "bg-surface sticky left-0 z-[1]",
        },
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex flex-col gap-3 pt-2">
              <div className="text-title-sm-bold text-dark uppercase">
                {formatDate(row.dateTime)}
              </div>
              <div className="text-title text-secondary">
                {formatTime(row.dateTime)}
              </div>
            </div>
          );
        },
      },
      {
        header: "game",
        maxSize: 108,
        enableSorting: false,
        minWidth: "96px",
        meta: {
          classNames: "bg-surface sticky left-[80px] z-[1]",
        },
        cell: (info) => {
          const row = info.row.original;
          return (
            <Link href={createGameDetailSlug(sport, row.gameId)}>
              <div className="flex flex-col gap-4 ">
                <div className="flex flex-row items-center gap-1">
                  <TeamDisplay
                    team={row.awayTeam}
                    size="sm"
                    variant="abbreviation"
                  />
                </div>
                <div className="flex flex-row items-center gap-1">
                  <TeamDisplay
                    team={row.homeTeam}
                    size="sm"
                    variant="abbreviation"
                  />
                </div>
              </div>
            </Link>
          );
        },
      },
      ...uniqueSportsbooks.map((sportsbookName) =>
        buildSportsbookColumn("", sportsbookName)
      ),
    ],
    [uniqueSportsbooks]
  );

  function buildSportsbookColumn(
    sportsbookLogoUrl: string,
    label: string
    // odds: GameOdds,
  ): ColumnDef<PreGameOdds> {
    return {
      id: label,
      size: 132,
      header: (_) => {
        return (
          <div className="flex flex-row gap-4 w-full justify-center items-center">
            {/* <img src={sportsbookLogoUrl} alt={label} /> */}
            <span className="text-title-sm-medium normal-case text-dark">
              {label}
            </span>
          </div>
        );
      },
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col gap-3 items-center w-full">
            {(displayingOdds === "spread" || displayingOdds === "all") && (
              <>
                <SportsbookOddsCell
                  gameId={row.gameId}
                  odd={buildOdd(
                    LineType.Spread,
                    GameAlignmentType.Away,
                    row.odds.find((o) => o.sportsbook === label)
                  )}
                  sportsbookUrl={
                    info.row.original.odds.find((o) => o.sportsbook === label)
                      ?.sportsbookUrl ||
                    SPORTSBOOK_URL_MAP[
                      info.row.original.odds.find((o) => o.sportsbook === label)
                        ?.sportsbook || ""
                    ] ||
                    undefined
                  }
                />
                <SportsbookOddsCell
                  gameId={row.gameId}
                  odd={buildOdd(
                    LineType.Spread,
                    GameAlignmentType.Home,
                    row.odds.find((o) => o.sportsbook === label)
                  )}
                  sportsbookUrl={
                    info.row.original.odds.find((o) => o.sportsbook === label)
                      ?.sportsbookUrl ||
                    SPORTSBOOK_URL_MAP[
                      info.row.original.odds.find((o) => o.sportsbook === label)
                        ?.sportsbook || ""
                    ] ||
                    undefined
                  }
                />
              </>
            )}
            {(displayingOdds === "moneyline" || displayingOdds === "all") && (
              <>
                <SportsbookOddsCell
                  gameId={row.gameId}
                  odd={buildOdd(
                    LineType.Moneyline,
                    GameAlignmentType.Away,
                    row.odds.find((o) => o.sportsbook === label)
                  )}
                  sportsbookUrl={
                    info.row.original.odds.find((o) => o.sportsbook === label)
                      ?.sportsbookUrl ||
                    SPORTSBOOK_URL_MAP[
                      info.row.original.odds.find((o) => o.sportsbook === label)
                        ?.sportsbook || ""
                    ] ||
                    undefined
                  }
                />
                <SportsbookOddsCell
                  gameId={row.gameId}
                  odd={buildOdd(
                    LineType.Moneyline,
                    GameAlignmentType.Home,
                    row.odds.find((o) => o.sportsbook === label)
                  )}
                  sportsbookUrl={
                    info.row.original.odds.find((o) => o.sportsbook === label)
                      ?.sportsbookUrl ||
                    SPORTSBOOK_URL_MAP[
                      info.row.original.odds.find((o) => o.sportsbook === label)
                        ?.sportsbook || ""
                    ] ||
                    undefined
                  }
                />
              </>
            )}
            {(displayingOdds === "total" || displayingOdds === "all") && (
              <>
                <SportsbookOddsCell
                  gameId={row.gameId}
                  odd={buildOdd(
                    LineType.Total,
                    GameAlignmentType.Away,
                    row.odds.find((o) => o.sportsbook === label)
                  )}
                  sportsbookUrl={
                    info.row.original.odds.find((o) => o.sportsbook === label)
                      ?.sportsbookUrl ||
                    SPORTSBOOK_URL_MAP[
                      info.row.original.odds.find((o) => o.sportsbook === label)
                        ?.sportsbook || ""
                    ] ||
                    undefined
                  }
                />
                <SportsbookOddsCell
                  gameId={row.gameId}
                  odd={buildOdd(
                    LineType.Total,
                    GameAlignmentType.Home,
                    row.odds.find((o) => o.sportsbook === label)
                  )}
                  sportsbookUrl={
                    info.row.original.odds.find((o) => o.sportsbook === label)
                      ?.sportsbookUrl ||
                    SPORTSBOOK_URL_MAP[
                      info.row.original.odds.find((o) => o.sportsbook === label)
                        ?.sportsbook || ""
                    ] ||
                    undefined
                  }
                />
              </>
            )}
          </div>
        );
      },
    };
  }
  return (
    <Table
      type="separator"
      columns={columns}
      data={processedData}
      className="w-auto"
      cellVAlign="top"
      scrollHint={{ left: stickyCol1 + stickyCol2 }}
    />
  );
}
